import { Controller, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, FieldErrors } from "react-hook-form";
import { AddOnTextInput, Button, Card, IconButton, RadioInput, TextInput, Typography } from "@/components/atoms";
import { extraTypeOptions } from "../../fixtures";
import { OtherChargesFormData, FormData, FormRegister, FormErrors, FormControl } from "../types";

interface OtherChargesCardProps {
  register: FormRegister<OtherChargesFormData>;
  control: FormControl<OtherChargesFormData>;
  errors: FormErrors<OtherChargesFormData>;
  fields: FieldArrayWithId<FormData, "otherCharges", "id">[];
  append: UseFieldArrayAppend<FormData, "otherCharges">;
  remove: UseFieldArrayRemove;
}

interface OtherChargeItemProps {
  control: FormControl<OtherChargesFormData>;
  register: FormRegister<OtherChargesFormData>;
  index: number;
  errors?: FieldErrors<{
    description?: string;
    amount?: number;
    type?: string;
  }>;
  remove: (index: number) => void;
}

export const OtherChargesCard = ({ register, control, errors, fields, append, remove }: OtherChargesCardProps) => {
  return (
    <div>
      <div className=" flex items-center justify-between">
        <Typography variant="title" className="block">
          Other Charges
        </Typography>
        <Button
          startIcon="Add"
          variant="tertiary-light"
          className="self-end text-info hover:bg-transparent hover:text-info-dark"
          onClick={() => append({ amount: NaN, description: "", type: "general" })}
        >
          Add Charge
        </Button>
      </div>

      <div className="space-y-4">
        {fields.map((field, index) => (
          <OtherChargeItem
            key={field.id}
            index={index}
            control={control}
            register={register}
            remove={remove}
            errors={errors.otherCharges && Array.isArray(errors.otherCharges) ? errors.otherCharges[index] : undefined}
          />
        ))}

        {fields.length === 0 && (
          <div className="py-8 text-center text-neutral-dark-gray">
            <Typography>No other charges added yet</Typography>
          </div>
        )}
      </div>
    </div>
  );
};

const OtherChargeItem = ({ control, register, index, remove, errors }: OtherChargeItemProps) => {
  return (
    <Card as="fieldset">
      <div className="mb-3 flex items-center justify-between">
        <Typography variant="title" className="font-medium">
          Charge #{index + 1}
        </Typography>
        <IconButton iconName="Trash" variant="custom" onClick={() => remove(index)} iconSize="lg" className="text-danger" />
      </div>

      <div className="space-y-3">
        <div>
          <Typography variant="action" className="mb-1 text-neutral-dark-gray">
            Charges Description
          </Typography>
          <TextInput {...register(`otherCharges.${index}.description`)} placeholder="Enter Description" />
          {errors?.description && <Typography className="mt-1 text-sm text-danger">{errors.description.message}</Typography>}
        </div>

        <div>
          <Typography variant="action" className=" text-neutral-dark-gray">
            Amount
          </Typography>
          <AddOnTextInput
            {...register(`otherCharges.${index}.amount`, { valueAsNumber: true })}
            startAddOn="AUD $"
            placeholder="0.00"
            type="number"
            step="0.01"
            className="[&_[data-headlessui-state]]:flex-1"
          />
          {errors?.amount && <Typography className="mt-1 text-sm text-danger">{errors.amount.message}</Typography>}
        </div>

        <div>
          <Controller
            name={`otherCharges.${index}.type`}
            control={control}
            render={({ field }) => (
              <div className="flex gap-6">
                {extraTypeOptions.map((option) => (
                  <RadioInput
                    key={option.value}
                    htmlFor={`other-charges-${index}-${option.value}`}
                    label={option.name}
                    value={option.value}
                    name={`otherCharges.${index}.type`}
                    onChange={({ target }) => field.onChange(target.value)}
                    checked={field.value === option.value}
                  />
                ))}
              </div>
            )}
          />
        </div>
      </div>
    </Card>
  );
};
